<!-- src/components/new-tip-v2/TiersProgress.vue -->
<!--
  Affichage en lecture seule des paliers d'une affectation face à la quantité
  relevée lors d'une visite.

  Rend visible la règle de gain : plusieurs paliers peuvent être franchis, mais
  un seul donne son gain — le plus haut. Les paliers franchis sont cochés, et
  celui qui a effectivement payé est mis en avant.

  `achievedTier` vient du snapshot de la visite (new_tip_v2_visits), pas d'un
  recalcul : si les paliers de l'affectation ont changé depuis la soumission,
  c'est bien le palier d'origine qui reste affiché comme payant.
-->
<template>
  <div>
    <div class="d-flex align-center mb-2">
      <span class="text-body-2 mr-2">Stock relevé :</span>
      <strong class="text-h6">{{ formatNumber(stockQty) }}</strong>
      <v-chip
        class="ml-3" small label
        :color="achievedTier ? tierColor(achievedTier) : 'grey'"
        outlined
      >
        {{ tierLabel(achievedTier) }}
      </v-chip>
    </div>

    <v-simple-table dense>
      <thead>
        <tr>
          <th>Palier</th>
          <th class="text-right">Objectif</th>
          <th class="text-right">Gain</th>
          <th class="text-center">Franchi</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="t in tiers"
          :key="t.tier"
          :class="{ 'paying-row': t.tier === achievedTier }"
        >
          <td>Palier {{ t.tier }}</td>
          <td class="text-right">{{ formatNumber(t.qty) }}</td>
          <td class="text-right">{{ formatNumber(t.gain) }} pts</td>
          <td class="text-center">
            <v-icon v-if="t.reached" small color="success">mdi-check-circle</v-icon>
            <v-icon v-else small color="grey lighten-1">mdi-circle-outline</v-icon>
          </td>
          <td>
            <v-chip v-if="t.tier === achievedTier" x-small label color="success">
              gain retenu
            </v-chip>
            <span
              v-else-if="t.reached"
              class="text-caption grey--text"
            >
              franchi, non retenu
            </span>
          </td>
        </tr>
        <tr v-if="tiers.length === 0">
          <td colspan="5" class="text-center grey--text text-caption">
            Aucun palier défini sur cette affectation.
          </td>
        </tr>
      </tbody>
    </v-simple-table>

    <v-alert
      v-if="tiers.length && achievedTier === null"
      type="info" dense text class="mt-3 mb-0"
    >
      Aucun palier franchi : la visite est valide mais ne rapporte aucun gain
      d'objectif.
      <template v-if="missingForFirstTier !== null">
        Il manquait {{ formatNumber(missingForFirstTier) }} unité(s) pour le palier 1.
      </template>
    </v-alert>

    <v-alert
      v-else-if="nextTier"
      type="info" dense text class="mt-3 mb-0"
    >
      Il manquait {{ formatNumber(nextTier.missing) }} unité(s) pour atteindre le
      palier {{ nextTier.tier }} ({{ formatNumber(nextTier.gain) }} pts).
    </v-alert>
  </div>
</template>

<script>
import { formatNumber } from '@/helpers/formatters';
import { tierLabel, tierColor } from '@/constants/newTipV2';

export default {
  name: 'NewTipV2TiersProgress',
  props: {
    /**
     * Affectation portant les paliers (objective_N_qty / objective_N_gain).
     */
    assignment: { type: Object, default: null },
    stockQty: { type: Number, default: 0 },
    /** Palier retenu d'après le snapshot de la visite ; null si aucun. */
    achievedTier: { type: Number, default: null },
  },
  computed: {
    tiers() {
      if (!this.assignment) return [];
      return [1, 2, 3]
        .map(tier => ({
          tier,
          qty: this.assignment[`objective_${tier}_qty`],
          gain: this.assignment[`objective_${tier}_gain`],
        }))
        .filter(t => t.qty !== null && t.qty !== undefined
                  && t.gain !== null && t.gain !== undefined)
        .map(t => ({ ...t, reached: Number(this.stockQty) >= Number(t.qty) }));
    },
    /** Premier palier non franchi, et ce qu'il manquait pour l'atteindre. */
    nextTier() {
      const next = this.tiers.find(t => !t.reached);
      if (!next) return null;
      return { ...next, missing: Number(next.qty) - Number(this.stockQty) };
    },
    missingForFirstTier() {
      if (!this.tiers.length) return null;
      return Number(this.tiers[0].qty) - Number(this.stockQty);
    },
  },
  methods: { formatNumber, tierLabel, tierColor },
};
</script>

<style scoped>
.paying-row { background-color: rgba(76, 175, 80, 0.08); }
.paying-row td { font-weight: 500; }
</style>
